import { useCallback, useEffect } from "react";
import Pagination from "../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import { formatCurrencyForTable, formatDate } from "../../../lib/utils";
import {
  ExpenseItem,
  ExpenseItemSearchRequest,
  ExpenseItemSearchResponse,
} from "../@types";
import { useGetExpenseItemsMutation } from "../financeApiSlice";

interface ExpenseDashTabProps {
  requestBody: ExpenseItemSearchRequest;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const ExpenseDashTab: React.FC<ExpenseDashTabProps> = ({
  requestBody,
  onPageChange,
  onPageSizeChange,
}) => {
  const [getExpenseItems, { data: usersData, isLoading, error: loadError }] =
    useGetExpenseItemsMutation();

  const fetchExpenseItems = useCallback(async () => {
    try {
      await getExpenseItems(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch expense items:", err);
    }
  }, [getExpenseItems, requestBody]);

  useEffect(() => {
    fetchExpenseItems();
  }, [fetchExpenseItems]);

  // Table columns configuration
  const columns: Column<ExpenseItem>[] = [
    {
      key: "tripNumber",
      header: "Trip number",
      width: "200px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "createdOn",
      header: "Date",
      width: "200px",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },

    {
      key: "amount",
      header: "Amount (NGN)",
      width: "200px",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "category",
      header: "Category",
      width: "200px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "description",
      header: "Description",
      width: "300px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "expenseType",
      header: "Type",
      width: "150px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
  ];

  const response = usersData?.data as ExpenseItemSearchResponse | undefined;

  return (
    <div className="overflow-hidden space-y-6">
      <Table
        data={response?.data || []}
        columns={columns}
        loading={isLoading}
        error={loadError}
        emptyMessage="No expense items found"
      />

      {/* Pagination */}
      {response && (
        <Pagination
          currentPage={response.currentPage}
          totalPages={response.totalPages}
          pageSize={response.pageSize}
          totalCount={response.totalCount}
          hasNextPage={response.hasNextPage}
          hasPreviousPage={response.hasPreviousPage}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};

export default ExpenseDashTab;
